import React from "react";
import { Link, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";

const ICON_PATHS = {
  notes: (
    <>
      <path d="M7 3h7l4 4v14H7z" />
      <path d="M14 3v5h5M10 13h5M10 17h5" />
    </>
  ),
  spark: (
    <>
      <path d="m12 3 1.4 4.1L17.5 9l-4.1 1.4L12 14.5l-1.4-4.1L6.5 9l4.1-1.9z" />
      <path d="m18.5 15 .7 2.1 2.1.7-2.1.7-.7 2.1-.7-2.1-2.1-.7 2.1-.7z" />
    </>
  ),
  signal: (
    <>
      <path d="M5 19a10 10 0 0 1 14 0M8 16a6 6 0 0 1 8 0M11 13a2 2 0 0 1 2 0" />
    </>
  ),
  edit: (
    <>
      <path d="M4 20h4l11-11-4-4L4 16zM13.5 6.5l4 4" />
    </>
  ),
  client: (
    <>
      <circle cx="12" cy="8" r="4" />
      <path d="M5 21a7 7 0 0 1 14 0" />
    </>
  ),
  chart: (
    <>
      <path d="M4 20V10M10 20V4M16 20v-7M22 20H2" />
    </>
  ),
  key: (
    <>
      <circle cx="8" cy="15" r="4" />
      <path d="m11 12 8-8M16 7l2 2M14 9l2 2" />
    </>
  ),
  users: (
    <>
      <circle cx="9" cy="8" r="3" />
      <path d="M3 20a6 6 0 0 1 12 0M16 7a3 3 0 0 1 0 6M17 15a5 5 0 0 1 4 5" />
    </>
  ),
  megaphone: (
    <>
      <path d="m4 13 12-6v10L4 13zM4 13v5h4v-3M16 10h3M18 6l2-2M18 18l2 2" />
    </>
  ),
};

function NavIcon({ name }) {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {ICON_PATHS[name]}
    </svg>
  );
}

const ADMIN_SECTIONS = [
  { section: "ai-agent", icon: "spark", label: "AI Agent Notes" },
  { section: "ai-agent-evv", icon: "signal", label: "AI Agent T&A" },
  { section: "create-note", icon: "edit", label: "Create Note" },
  { section: "clients", icon: "client", label: "Clients" },
  { section: "notes", icon: "chart", label: "All Notes" },
  { section: "invite-codes", icon: "key", label: "Invite Codes" },
  { section: "users", icon: "users", label: "Users" },
];

export default function Sidebar({ collapsed, onToggle }) {
  const { user, logout } = useAuth();
  const location = useLocation();
  const currentSection =
    new URLSearchParams(location.search).get("section") || "clients";
  const isOnAdmin = location.pathname === "/admin";
  const initials =
    [user?.firstName?.[0], user?.lastName?.[0]]
      .filter(Boolean)
      .join("")
      .toUpperCase() || "?";

  return (
    <aside
      className={`sidebar enterprise-sidebar${collapsed ? " sidebar--collapsed" : ""}`}
    >
      <div className="sidebar-header enterprise-sidebar-header">
        <div className="sidebar-logo">
          <div className="enterprise-logo-mark">H</div>
          {!collapsed && (
            <div className="sidebar-brand-text">
              <span className="sidebar-brand-name">HCA Notes</span>
              <span className="sidebar-brand-sub">Care documentation</span>
            </div>
          )}
        </div>
        <button
          className="sidebar-toggle-btn"
          onClick={onToggle}
          title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
          aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
        >
          {collapsed ? "›" : "‹"}
        </button>
      </div>

      <nav className="sidebar-nav" aria-label="Primary navigation">
        <Link
          to="/dashboard"
          className={`sidebar-nav-item${location.pathname === "/dashboard" ? " sidebar-nav-item--active" : ""}`}
          title={collapsed ? "Progress Notes" : undefined}
        >
          <span className="sidebar-nav-icon">
            <NavIcon name="notes" />
          </span>
          {!collapsed && (
            <span className="sidebar-nav-label">Progress Notes</span>
          )}
        </Link>

        {user?.isAdmin && (
          <>
            {!collapsed && (
              <div className="sidebar-section-label">Administration</div>
            )}
            {collapsed && <div className="sidebar-section-divider" />}
            {ADMIN_SECTIONS.map(({ section, icon, label }) => (
              <Link
                key={section}
                to={`/admin?section=${section}`}
                className={`sidebar-nav-item sidebar-nav-item--sub${isOnAdmin && currentSection === section ? " sidebar-nav-item--active" : ""}`}
                title={collapsed ? label : undefined}
              >
                <span className="sidebar-nav-icon">
                  <NavIcon name={icon} />
                </span>
                {!collapsed && (
                  <span className="sidebar-nav-label">{label}</span>
                )}
              </Link>
            ))}
          </>
        )}
      </nav>

      {user?.isAdmin && (
        <a
          href="https://admin.homecareofmi.com"
          target="_blank"
          rel="noreferrer"
          className="sidebar-nav-item enterprise-external-link"
          title="Marketing Admin"
        >
          <span className="sidebar-nav-icon">
            <NavIcon name="megaphone" />
          </span>
          {!collapsed && (
            <span className="sidebar-nav-label">Marketing Admin</span>
          )}
        </a>
      )}

      <div className="sidebar-spacer" />
      <div className="sidebar-footer">
        <div className="sidebar-user-row">
          <div className="sidebar-avatar">{initials}</div>
          {!collapsed && (
            <div className="sidebar-user-info">
              <div className="sidebar-user-name">
                {user?.firstName} {user?.lastName}
              </div>
              <div className="sidebar-user-role">
                {user?.isAdmin ? "Administrator" : "Caregiver"}
              </div>
            </div>
          )}
        </div>
        <button
          className={`sidebar-logout${collapsed ? " sidebar-logout--icon" : ""}`}
          onClick={logout}
          title="Sign out"
        >
          {collapsed ? "↩" : "Sign out"}
        </button>
      </div>
    </aside>
  );
}
